'use client';

/**
 * Рабочее место передачи файлов.
 *
 * Одна страница на две роли. Роль определяется адресом: пришли с
 * идентификатором комнаты в ссылке — вы получатель, открыли начисто —
 * отправитель. Отдельная страница для получателя не нужна, а лишний адрес
 * пришлось бы объяснять в интерфейсе.
 *
 * Ключ шифрования живёт во фрагменте ссылки (после `#`). Фрагмент браузер
 * на сервер не отправляет, поэтому расшифровать файлы может только тот,
 * кому ссылку передали.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Check,
  Copy,
  Download,
  FileUp,
  Link2,
  Loader2,
  ShieldAlert,
  Upload,
  Users,
} from 'lucide-react';
import { makeSecret } from '@/lib/transfer/channel';
import { useTransfer, type FileProgress } from '@/lib/transfer/use-transfer';
import { QrLink } from '@/components/datahunter/qr-link';

/** Человеческий размер файла. */
function humanSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;

  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }

  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit] ?? ''}`;
}

export function FileTransfer({ title }: { title: string }) {
  const t = useTranslations('dh_transfer_ui');

  const [room, setRoom] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [role, setRole] = useState<'host' | 'guest' | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkBroken, setLinkBroken] = useState(false);
  /**
   * Доступно ли шифрование.
   *
   * Web Crypto работает только в безопасном контексте: по HTTPS или на
   * localhost. В проде это всегда так, но по обычному HTTP на постороннем
   * имени шифрования нет, и передавать файлы в открытую мы не станем.
   */
  const [insecure, setInsecure] = useState(false);

  // Роль читается один раз при открытии: дальше она не меняется, а
  // перечитывание адреса после навигации сбросило бы идущую передачу.
  useEffect(() => {
    if (!window.isSecureContext) {
      setInsecure(true);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const id = params.get('room');

    if (!id) return;

    const fragment = window.location.hash.replace(/^#/, '');

    // Ссылка без фрагмента бесполезна: без ключа расшифровать нечем.
    // Честно говорим об этом, а не показываем вечное ожидание.
    if (!fragment) {
      setLinkBroken(true);
      return;
    }

    setRoom(id);
    setSecret(fragment);
    setRole('guest');
  }, []);

  /** Создаёт комнату и ссылку для получателя. */
  const share = useCallback(async () => {
    if (files.length === 0) return;

    const id = crypto.randomUUID();
    const key = makeSecret();
    const hostId = crypto.randomUUID();

    const response = await fetch('/api/transfer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, hostId }),
    });

    if (!response.ok) return;

    setRoom(id);
    setSecret(key);
    setRole('host');
  }, [files]);

  const link = useMemo(() => {
    if (!room || !secret) return '';
    const url = new URL(window.location.href);
    url.search = `?room=${room}`;
    url.hash = secret;
    return url.toString();
  }, [room, secret]);

  if (linkBroken) {
    return (
      <Shell title={title}>
        <Notice tone="danger" icon={<ShieldAlert className="w-5 h-5" />} text={t('no_secret')} />
      </Shell>
    );
  }

  if (insecure) {
    return (
      <Shell title={title}>
        <Notice
          tone="danger"
          icon={<ShieldAlert className="w-5 h-5" />}
          title={t('insecure_title')}
          text={t('insecure_hint')}
        />
      </Shell>
    );
  }

  if (!room || !secret || !role) {
    return (
      <Shell title={title}>
        <Picker
          files={files}
          dragging={dragging}
          onDragging={setDragging}
          onFiles={setFiles}
          onShare={() => {
            void share();
          }}
        />
      </Shell>
    );
  }

  return (
    <Shell title={title}>
      <Session
        roomId={room}
        secret={secret}
        role={role}
        files={files}
        link={link}
        copied={copied}
        onCopy={() => {
          void navigator.clipboard.writeText(link).then(() => {
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 2000);
          });
        }}
      />
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  const t = useTranslations('dh_transfer_ui');

  return (
    <div className="max-w-[880px] mx-auto px-6 py-10">
      <h1 className="text-2xl font-bold mb-6">{title}</h1>
      {children}
      <p className="mt-8 text-xs text-[var(--color-text-muted)] leading-relaxed">
        {t('privacy_note')}
      </p>
    </div>
  );
}

/** Выбор файлов до создания ссылки. */
function Picker({
  files,
  dragging,
  onDragging,
  onFiles,
  onShare,
}: {
  files: File[];
  dragging: boolean;
  onDragging: (value: boolean) => void;
  onFiles: (files: File[]) => void;
  onShare: () => void;
}) {
  const t = useTranslations('dh_transfer_ui');
  const input = useRef<HTMLInputElement>(null);

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          onDragging(true);
        }}
        onDragLeave={() => {
          onDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          onDragging(false);
          onFiles([...files, ...Array.from(e.dataTransfer.files)]);
        }}
        className={`rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${
          dragging
            ? 'border-[var(--color-text)] bg-[var(--color-bg-elevated)]'
            : 'border-[var(--color-border)]'
        }`}
      >
        <Upload className="w-8 h-8 mx-auto mb-4 text-[var(--color-text-muted)]" />

        <button
          onClick={() => input.current?.click()}
          className="dh-cta px-6 py-2.5 rounded-full font-medium transition-colors"
        >
          {t('pick')}
        </button>

        <p className="mt-3 text-sm text-[var(--color-text-muted)]">
          {dragging ? t('drop_here') : t('pick_hint')}
        </p>

        <input
          ref={input}
          type="file"
          multiple
          className="sr-only"
          aria-label={t('pick')}
          onChange={(e) => {
            onFiles([...files, ...Array.from(e.target.files ?? [])]);
          }}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-6">
          <p className="text-sm font-medium mb-3">{t('files_count', { count: files.length })}</p>
          <ul className="rounded-xl border border-[var(--color-border)] divide-y divide-[var(--color-border)] overflow-hidden mb-6">
            {files.map((file) => (
              <li
                key={`${file.name}:${String(file.size)}:${String(file.lastModified)}`}
                className="flex items-center gap-3 px-4 py-2.5 text-sm"
              >
                <FileUp className="w-4 h-4 text-[var(--color-text-muted)] shrink-0" />
                <span className="truncate">{file.name}</span>
                <span className="ml-auto text-xs text-[var(--color-text-muted)] shrink-0">
                  {humanSize(file.size)}
                </span>
              </li>
            ))}
          </ul>

          <button
            onClick={onShare}
            className="dh-cta inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-colors"
          >
            <Link2 className="w-4 h-4" />
            {t('create_link')}
          </button>
        </div>
      )}
    </div>
  );
}

/** Идущая передача: ссылка, состояние соединения и ход по файлам. */
function Session({
  roomId,
  secret,
  role,
  files,
  link,
  copied,
  onCopy,
}: {
  roomId: string;
  secret: string;
  role: 'host' | 'guest';
  files: File[];
  link: string;
  copied: boolean;
  onCopy: () => void;
}) {
  const t = useTranslations('dh_transfer_ui');
  const { phase, progress, error, resumed } = useTransfer({ roomId, secret, role, files });

  if (phase === 'refused') {
    return (
      <Notice
        tone="danger"
        icon={<Users className="w-5 h-5" />}
        title={t('refused_title')}
        text={t('refused_hint')}
      />
    );
  }

  return (
    <div>
      {role === 'host' && (
        <div className="rounded-2xl border border-[var(--color-border)] p-5 mb-6">
          <p className="text-sm font-medium mb-2">{t('link_title')}</p>

          {/*
            Ссылка и QR закрывают один шаг разными способами:
            скопировать удобно на компьютере, снять камерой — на
            телефоне. Без QR человеку, начавшему с телефона, приходилось
            отправлять ссылку самому себе через мессенджер, то есть
            пользоваться ровно тем, от чего инструмент избавляет (#175).

            QR кодирует `link`, а не адрес страницы: в строке браузера
            отправителя нет ни комнаты, ни ключа после решётки.
          */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <code className="flex-1 min-w-0 truncate font-mono text-xs px-3 py-2 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
                  {link}
                </code>
                <button
                  onClick={onCopy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--color-border)] text-sm hover:border-[var(--color-text-muted)] transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? t('copied') : t('copy_link')}
                </button>
              </div>

              <p className="mt-3 text-xs text-[var(--color-text-muted)] leading-relaxed">
                {t('link_hint')}
              </p>
            </div>

            <div className="shrink-0 text-center">
              <QrLink value={link} />
              {/*
                Предупреждение повторяется под кодом, а не только под
                ссылкой: ключ шифрования попадает и в QR, а человек,
                показывающий код с экрана, текст сбоку не читает.
              */}
              <p className="mt-2 max-w-[160px] text-[11px] text-[var(--color-text-muted)] leading-snug">
                {t('qr_hint')}
              </p>
            </div>
          </div>
        </div>
      )}

      <Status phase={phase} role={role} error={error} />

      {resumed && (
        <p className="mb-4 text-sm text-[var(--color-text-muted)]" data-testid="resumed">
          {t('resumed')}
        </p>
      )}

      {progress.length > 0 && <Progress items={progress} />}

      {phase === 'transferring' || phase === 'connected' || phase === 'waiting' ? (
        <p className="mt-6 text-xs text-[var(--color-text-muted)]">{t('keep_open')}</p>
      ) : null}
    </div>
  );
}

function Status({
  phase,
  role,
  error,
}: {
  phase: string;
  role: 'host' | 'guest';
  error: string | null;
}) {
  const t = useTranslations('dh_transfer_ui');

  if (phase === 'failed') {
    return (
      <Notice
        tone="danger"
        icon={<ShieldAlert className="w-5 h-5" />}
        title={t('failed_title')}
        text={error === 'decrypt-failed' ? t('decrypt_failed') : t('failed_hint')}
      />
    );
  }

  const spinner = phase === 'waiting' || phase === 'connecting' || phase === 'transferring';

  const text =
    phase === 'waiting'
      ? t('waiting')
      : phase === 'connecting'
        ? t('connecting')
        : phase === 'transferring'
          ? t('transferring')
          : phase === 'done'
            ? role === 'host'
              ? t('done_send')
              : t('done_receive')
            : t('connected');

  return (
    <div className="flex items-center gap-2 mb-4 text-sm" role="status" aria-live="polite">
      {spinner ? (
        <Loader2 className="w-4 h-4 animate-spin text-[var(--color-text-muted)]" />
      ) : (
        <Check className="w-4 h-4 text-[var(--color-success-500)]" />
      )}
      <span>{text}</span>
      {phase === 'waiting' && (
        <span className="text-[var(--color-text-muted)]">{t('waiting_hint')}</span>
      )}
    </div>
  );
}

/** Ход передачи по каждому файлу. */
function Progress({ items }: { items: FileProgress[] }) {
  const t = useTranslations('dh_transfer_ui');

  return (
    <ul
      className="rounded-xl border border-[var(--color-border)] divide-y divide-[var(--color-border)] overflow-hidden"
      data-testid="transfer-progress"
    >
      {items.map((item) => {
        const percent = item.meta.size === 0 ? 100 : Math.floor((item.sent / item.meta.size) * 100);

        return (
          <li key={item.meta.id} className="px-4 py-3">
            <div className="flex items-center gap-3 text-sm mb-2">
              <span className="truncate">{item.meta.name}</span>

              <span className="ml-auto text-xs text-[var(--color-text-muted)] shrink-0">
                {humanSize(item.sent)} / {humanSize(item.meta.size)}
              </span>

              {item.blob && (
                <a
                  href={URL.createObjectURL(item.blob)}
                  download={item.meta.name}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-text-muted)] transition-colors shrink-0"
                >
                  <Download className="w-3.5 h-3.5" />
                  {t('download')}
                </a>
              )}
            </div>

            <div
              className="h-1.5 rounded-full bg-[var(--color-bg-elevated)] overflow-hidden"
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={item.meta.name}
            >
              <div
                className="h-full bg-[var(--color-text)] transition-[width] duration-200"
                style={{ width: `${String(percent)}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Notice({
  tone,
  icon,
  title,
  text,
}: {
  tone: 'danger' | 'muted';
  icon: React.ReactNode;
  title?: string;
  text: string;
}) {
  return (
    <div
      className={`rounded-2xl border p-5 flex gap-3 ${
        tone === 'danger' ? 'border-[var(--color-danger-500)]/40' : 'border-[var(--color-border)]'
      }`}
      role="status"
    >
      <span className="shrink-0 text-[var(--color-text-muted)]">{icon}</span>
      <div>
        {title && <p className="font-medium mb-1">{title}</p>}
        <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">{text}</p>
      </div>
    </div>
  );
}
